import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CheckCircle, MailX, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { API_URL, formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

export const EmailHistoryCard = ({ personId }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/email-marketing/person/${personId}/history`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], failed_count: 0 }));
  }, [personId]);

  if (!data || (!data.items.length && !data.unsubscribed_at)) return null;

  return (
    <Card data-testid="email-history-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="w-5 h-5 text-[#2C9DE1]" /> Marketing Emails
          {data.failed_count > 0 && (
            <Badge className="bg-red-100 text-red-700 ml-2" data-testid="email-history-failed-badge">
              <AlertTriangle className="w-3 h-3 mr-1" /> {data.failed_count} failed
            </Badge>
          )}
        </CardTitle>
        <CardDescription>Campaigns sent to this contact</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.unsubscribed_at && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="email-history-unsubscribed">
            <MailX className="w-4 h-4" /> Unsubscribed from marketing emails on {formatDate(data.unsubscribed_at)}
          </div>
        )}
        {data.items.map((i) => (
          <div key={i.campaign_id} className={`flex items-start justify-between gap-4 p-3 rounded-lg border ${i.status === 'failed' ? 'border-red-200 bg-red-50' : 'border-slate-200'}`} data-testid={`email-history-${i.campaign_id}`}>
            <div className="min-w-0">
              {isAdmin
                ? <Link to={`/email-marketing/campaigns/${i.campaign_id}`} className="font-medium text-sm hover:underline">{i.campaign_name}</Link>
                : <p className="font-medium text-sm">{i.campaign_name}</p>}
              <p className="text-xs text-slate-500">{i.sent_at ? formatDate(i.sent_at) : '-'}{i.email ? ` · ${i.email}` : ''}</p>
              {i.status === 'failed' && <p className="text-xs text-red-600 mt-1 break-words">{i.error || 'Reason not recorded'}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {i.status === 'failed'
                ? <Badge className="bg-red-100 text-red-700">Failed</Badge>
                : i.status === 'sent' ? <Badge className="bg-green-100 text-green-700">Sent</Badge>
                : <Badge className="bg-slate-100 text-slate-600">Pending</Badge>}
              {i.opened && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Opened</span>}
              {i.clicked && <span className="text-xs text-blue-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Clicked</span>}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
